import { CalendarCheck, ClipboardList, HeartPulse } from "lucide-react";
import { waLink } from "../../lib/constants";
import useReveal from "../../hooks/useReveal";

const PASSOS = [
  {
    n: "01",
    icon: CalendarCheck,
    color: "#0D47A1",
    bg: "rgba(13, 71, 161, 0.08)",
    title: "Consulta Inicial e Avaliação",
    bullets: [
      "Você agenda sua consulta",
      "Leva todos os exames (radiografia, tomografia, ultrassom)",
      "Dr. Mateus avalia os achados com cuidado",
      "Discussão dos sintomas, possíveis causas e próximos passos",
    ],
    outcome: "Diagnóstico preliminar e plano de ação personalizado",
  },
  {
    n: "02",
    icon: ClipboardList,
    color: "#00897B",
    bg: "rgba(0, 137, 123, 0.08)",
    title: "Procedimento Diagnóstico",
    subtitle: "Toracocentese, Drenagem ou Pleuroscopia",
    bullets: [
      "Realização do procedimento para coleta da amostra diagnóstica",
      "Provável alívio dos sintomas durante o procedimento",
      "Resultado em 5–7 dias",
    ],
    outcome: "Identificação da causa do derrame pleural",
  },
  {
    n: "03",
    icon: HeartPulse,
    color: "#0D47A1",
    bg: "rgba(13, 71, 161, 0.08)",
    title: "Tratamento Etiológico",
    bullets: [
      "Com o diagnóstico definido, iniciamos o tratamento da causa do derrame",
      "Acompanhamento regular para avaliar possível recidiva",
      "Equipe multidisciplinar envolvida sempre que necessário",
    ],
    outcome: "Resolução do derrame e seguimento continuado",
  },
];

export default function Caminho() {
  const { ref, isVisible } = useReveal();

  return (
    <section
      id="caminho"
      ref={ref}
      className={`py-24 md:py-32 bg-[#F0F9F7] reveal-section ${isVisible ? "is-visible" : ""}`}
      data-testid="caminho-section"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center bg-[#00897B]/10 text-[#00897B] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            Seu Caminho Para Respirar Melhor
          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-6 leading-tight">
            Do Diagnóstico à Cura, Passo a Passo
          </h2>
          <p className="text-base md:text-lg text-[#212121]/70 leading-relaxed">
            Do momento em que você descobre o derrame até a cura, o processo é organizado e previsível.
            Aqui está exatamente o que vai acontecer.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {PASSOS.map((p, idx) => {
            const Icon = p.icon;
            return (
              <div
                key={idx}
                data-testid={`caminho-passo-${idx + 1}`}
                className="relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100"
              >
                {idx < PASSOS.length - 1 && (
                  <div className="hidden md:block absolute top-12 -right-3 w-6 h-0.5 bg-[#0D47A1]/20 z-10" />
                )}
                <div className="flex items-center justify-between mb-5">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: p.bg }}
                  >
                    <Icon size={24} style={{ color: p.color }} />
                  </div>
                  <span
                    className="font-poppins font-bold text-3xl"
                    style={{ color: p.color + "33" }}
                  >
                    {p.n}
                  </span>
                </div>
                <h3 className="font-poppins font-bold text-xl text-[#212121] mb-1">{p.title}</h3>
                {p.subtitle && (
                  <p className="text-[#0D47A1] text-sm font-semibold mb-3">{p.subtitle}</p>
                )}
                <ul className="space-y-2 mb-5">
                  {p.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-[#212121]/70 leading-relaxed"
                    >
                      <span className="text-[#00897B] font-bold mt-0.5">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-gray-100">
                  <div className="text-xs text-[#212121]/50 font-medium mb-1">Resultado</div>
                  <div
                    className="text-sm font-semibold leading-snug"
                    style={{ color: p.color }}
                  >
                    {p.outcome}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-3xl p-8 md:p-10 border-l-4 border-[#00897B] max-w-3xl mx-auto mb-10 shadow-sm">
          <p className="text-[#212121]/80 leading-relaxed text-base md:text-lg">
            Durante toda essa jornada, você tem o Dr. Mateus e toda sua equipe ao seu lado.{" "}
            <span className="font-semibold text-[#0D47A1]">
              Derrame pleural é tratável, e você não está sozinho nesse processo.
            </span>
          </p>
        </div>

        <div className="text-center">
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="caminho-cta-button"
            className="inline-flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold px-10 py-4 rounded-full hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5 hover:shadow-xl"
          >
            MARCAR MINHA AVALIAÇÃO INICIAL
          </a>
        </div>
      </div>
    </section>
  );
}
